import React, { useCallback, useEffect, useState } from "react";
import Select from "react-select";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { BtnSpinner } from "../../components/fields/BtnSpinner";
import { BASE_URLS } from "../../config/urls";
import { Notification } from "../../models/Notification";
import AdminLayout from "../components/AdminLayout";
import { SupplyDTO } from "../../models/SupplyDTO";
import { apiFetch } from "../../utils/api";
import "../styles/admin-notifications.css";
import { format } from "date-fns";

interface SelectOption {
    value: string | number;
    label: string;
}

const CITY_OPTIONS: SelectOption[] = [
    { value: "Самара", label: "Самара" },
    { value: "Тольятти", label: "Тольятти" },
];

const selectMenuPortal = {
    menuPortalTarget: document.body,
    menuPosition: "fixed" as const,
    styles: {
        menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
    },
};
const NotificationsAdminPage: React.FC = () => {
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [isPopupVisible, setIsPopupVisible] = useState(false);

    const [suppliesOptions, setSuppliesOptions] = useState<SelectOption[]>([]);
    const [selectedSupplies, setSelectedSupplies] = useState<SelectOption[]>([]);
    const [selectedCities, setSelectedCities] = useState<SelectOption[]>([]);
    const [formTitle, setFormTitle] = useState("");
    const [formDescription, setFormDescription] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        apiFetch(`${BASE_URLS.api}/admin/notifications`)
            .then((res) => res.json())
            .then((data: Notification[]) => {
                setNotifications(data);
            })
            .catch((err) => console.error("Ошибка загрузки уведомлений:", err));
    }, []);

    useEffect(() => {
        apiFetch(`${BASE_URLS.api}/schedule`)
            .then((res) => res.json())
            .then((data: SupplyDTO[]) => {
                const options: SelectOption[] = data.map((s) => {
                    let label = s.title;
                    if (s.departureDate) {
                        const date = format(new Date(s.departureDate), "dd.MM.yyyy");
                        label = `${s.title} (${date})`;
                    }
                    return {
                        value: s.id,
                        label,
                    };
                });
                setSuppliesOptions(options);
            })
            .catch((err) => console.error("Ошибка загрузки поставок:", err));
    }, []);

    const openCreateModal = () => {
        setFormTitle("");
        setFormDescription("");
        setSelectedSupplies([]);
        setSelectedCities([]);
        setIsPopupVisible(true);
        document.body.style.overflow = "hidden";
    };

    const closeCreateModal = () => {
        setIsPopupVisible(false);
        document.body.style.overflow = "auto";
    };

    const handleSelectAllCities = () => {
        setSelectedCities(CITY_OPTIONS);
    };

    const handleCreateNotification = useCallback(async () => {
        if (!formTitle.trim() || !formDescription.trim()) {
            alert("Заполните заголовок и описание");
            return;
        }
        if (selectedSupplies.length === 0) {
            alert("Выберите хотя бы одну поставку");
            return;
        }
        if (selectedCities.length === 0) {
            alert("Выберите хотя бы один город");
            return;
        }

        const body = {
            title: formTitle.trim(),
            description: formDescription.trim(),
            supplyIds: selectedSupplies.map((s) => s.value),
        };

        try {
            setIsSubmitting(true);
            const res = await apiFetch(`${BASE_URLS.api}/admin/notifications`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(body),
            });

            if (!res.ok) {
                throw new Error(`Ошибка создания уведомления: ${res.status}`);
            }

            const created: Notification = await res.json();
            setNotifications((prev) => [created, ...prev]);
            closeCreateModal();
        } catch (e) {
            console.error(e);
            alert("Не удалось создать уведомление");
        } finally {
            setIsSubmitting(false);
        }
    }, [formTitle, formDescription, selectedSupplies, selectedCities]);

    return (
        <AdminLayout isPopupVisible={isPopupVisible}>
            <HelmetProvider>
                <Helmet title="Уведомления" />
            </HelmetProvider>

            <div className="page_content notifications-page">
                <section className="admin-card" style={{ marginTop: 90 }}>
                    <h1 className="admin-heading">Уведомления</h1>
                    <p className="admin-subtitle">
                        Создание и просмотр push-уведомлений для клиентов по выбранным поставкам
                        и городам отправления.
                    </p>

                    <div className="notifications-header">
                        <button
                            type="button"
                            className="admin-button-primary"
                            onClick={openCreateModal}
                        >
                            + Добавить уведомление
                        </button>
                    </div>

                    <div className="admin-table-wrapper">
                        <table className="admin_table notifications-table">
                            <thead>
                            <tr>
                                <th>Заголовок</th>
                                <th>Описание</th>
                                <th>Поставки</th>
                                <th>Статус отправки</th>
                            </tr>
                            </thead>
                            <tbody>
                            {notifications.length === 0 && (
                                <tr>
                                    <td
                                        colSpan={5}
                                        className="admin-table-empty"
                                    >
                                        Уведомлений пока нет.
                                    </td>
                                </tr>
                            )}
                            {notifications.map((n) => (
                                <tr key={n.id}>
                                    <td>{n.title}</td>
                                    <td>{n.description}</td>
                                    <td>
                                        {n.supplies && n.supplies.length > 0
                                            ? n.supplies
                                                .map((s) => {
                                                    const title =
                                                        s.titleType?.title ?? `#${s.id}`;
                                                    let label = title;
                                                    if (s.departureDate) {
                                                        const date = format(
                                                            new Date(s.departureDate),
                                                            "dd.MM.yyyy"
                                                        );
                                                        label = `${title} (${date})`;
                                                    }
                                                    return label;
                                                })
                                                .join(", ")
                                            : "—"}
                                    </td>
                                    <td>
                                        <span
                                            className={
                                                n.sent
                                                    ? "notifications-status notifications-status--sent"
                                                    : "notifications-status notifications-status--pending"
                                            }
                                        >
                                            {n.sent ? "Отправлено" : "Не отправлено"}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </section>

                {/* Модальное окно добавления уведомления */}
                {isPopupVisible && (
                    <div className="notifications-modal-overlay" onClick={closeCreateModal}>
                        <div
                            className="notifications-modal"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h2 className="notifications-modal-title">
                                Создать уведомление
                            </h2>

                            <form
                                className="notifications-form"
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    handleCreateNotification();
                                }}
                            >
                                <div className="admin-field">
                                    <div className="admin_title">Заголовок</div>
                                    <input
                                        type="text"
                                        className="admin-input"
                                        value={formTitle}
                                        onChange={(e) => setFormTitle(e.target.value)}
                                        required
                                    />
                                </div>

                                <div className="admin-field">
                                    <div className="admin_title">Описание</div>
                                    <textarea
                                        className="admin-input notifications-textarea"
                                        rows={4}
                                        value={formDescription}
                                        onChange={(e) =>
                                            setFormDescription(e.target.value)
                                        }
                                        required
                                    />
                                </div>

                                <div className="admin-field">
                                    <div className="admin_title">Поставки</div>
                                    <Select {...selectMenuPortal}
                                        isMulti
                                        options={suppliesOptions}
                                        value={selectedSupplies}
                                        onChange={(value) =>
                                            setSelectedSupplies(value as SelectOption[])
                                        }
                                        classNamePrefix="notifications-select"
                                        placeholder="Выберите поставки..."
                                    />
                                </div>

                                <div className="admin-field">
                                    <div className="admin_title">
                                        Города отправления (Самара, Тольятти)
                                    </div>
                                    <div className="notifications-cities-row">
                                        <div className="notifications-cities-row__select">
                                            <Select {...selectMenuPortal}
                                                isMulti
                                                options={CITY_OPTIONS}
                                                value={selectedCities}
                                                onChange={(value) =>
                                                    setSelectedCities(
                                                        value as SelectOption[]
                                                    )
                                                }
                                                classNamePrefix="notifications-select"
                                                placeholder="Выберите города..."
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            className="admin-button-secondary admin-button-secondary--ghost"
                                            onClick={handleSelectAllCities}
                                        >
                                            Выбрать все
                                        </button>
                                    </div>
                                </div>

                                <div className="notifications-modal-actions">
                                    <button
                                        type="button"
                                        className="admin-button-secondary admin-button-secondary--ghost"
                                        onClick={closeCreateModal}
                                        disabled={isSubmitting}
                                    >
                                        Отмена
                                    </button>
                                    <button
                                        type="submit"
                                        className="admin-button-primary"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? <><BtnSpinner /></> : "Сохранить"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
};

export default NotificationsAdminPage;
